import { useAuth0 } from "@auth0/auth0-react"
import { useQuery } from "@tanstack/react-query"
import axios from "axios"
import { useSelector } from "react-redux"
import { usePermissionsList } from "../queries/usePermissions"

import { getDateFormatted, getFirstOpenDateToday } from "../../utils/generalUtils"
import {
	filterPatientScheduleForEditedPatients,
	filterNurseScheduleForEditedNurses,
	filterEditedPatients,
	filterEditedNurses
} from "../../utils/mongoUtils"
import useFetchSettings from "../useFetchSettings"

/**
 *
 * @returns
 */
const useScheduleFromBackend = (componentName = "default") => {
	const { user, isAuthenticated, getAccessTokenSilently } = useAuth0()

	const testEnvironment = user.org_id === "org_lkg3WMhi3Q2l92iT"

	const { settingsData } = useFetchSettings()
	const isEditing = useSelector(state => state.schedule.isEditing)
	const permissionsData = usePermissionsList()

	const documentId = useSelector(state => state.schedule.documentId)
	const currentPatientSchedule = useSelector(state => state.schedule.patientSchedule)
	const currentNurseSchedule = useSelector(state => state.schedule.nurseSchedule)
	const editedPatients = useSelector(state => state.schedule.editedPatients)
	const editedNurses = useSelector(state => state.schedule.editedNurses)

	const enabled = !!(
		isAuthenticated &&
		!isEditing &&
		settingsData &&
		settingsData.centerOpen &&
		settingsData.centerOpen.length > 0 &&
		permissionsData &&
		permissionsData.includes("read:schedules")
	)

	const queryFn = async () => {
		const centerClosedDates = settingsData?.center_closed_dates || []
		const date = getDateFormatted(
			getFirstOpenDateToday(settingsData?.centerOpen, testEnvironment, centerClosedDates)
		)

		const token = await getAccessTokenSilently({
			audience: process.env.REACT_APP_API_IDENTIFIER
		})

		const response = await axios.post(
			`${process.env.REACT_APP_BACKEND_URL}/api/get-schedule`,
			{
				documentId,
				date,
				currentPatientSchedule:
					filterPatientScheduleForEditedPatients(
						currentPatientSchedule,
						editedPatients
					) || [],
				currentNurseSchedule:
					filterNurseScheduleForEditedNurses(currentNurseSchedule, editedNurses) || [],
				editedPatients: filterEditedPatients(editedPatients) || [],
				editedNurses: filterEditedNurses(editedNurses) || []
			},
			{
				headers: { Authorization: `Bearer ${token}` }
			}
		)
		return response.data.payload
	}

	//This will auto retry for 5xx errors 3 times
	//see https://tanstack.com/query/latest/docs/framework/react/reference/useQuery#retry

	const { data, isLoading, error } = useQuery({
		queryKey: ["schedule", componentName],
		queryFn,
		enabled,
		refetchInterval: 1000 * 30 // Refetch every 30 seconds
	})

	return { data, isLoading, error }
}

export default useScheduleFromBackend
